import { format, isAfter, isBefore, parseISO } from "date-fns";
import React from "react";

import { Avatar, ICellRendererParams, Link, PYColumnDef, Text } from "@performyard/py-components";

import TableChip from "../../../../../../components/Chip/templates/TableChip";

import { Review } from "../../../../../../api/services/searchServiceResultTypes/reviews";

// utils
import toEmployeeUrl from "../../../../../../utils/URLs/Employee";

const NameCellRenderer = (params: ICellRendererParams<Review>) => {
	const review = params.data;
	if (!review) return null;

	const { name, _id, awaiting_approval_count } = review;
	const count360 = review["360_count"] || 0;

	return (
		<div className="flex h-full items-center justify-between">
			<Link href={`/view-review/${_id}/`}>
				<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
					{name}
				</Text>
			</Link>
			<div className="flex items-center justify-end gap-2">
				{count360 ? <TableChip color="blue" label="360 Enabled" /> : null}
				{awaiting_approval_count ? <TableChip color="green" label="Awaiting Approval" /> : null}
			</div>
		</div>
	);
};

const ReviewCycleCellRenderer = (params: ICellRendererParams<Review>) => {
	const review = params.data;
	if (!review) return null;

	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
				{review.bulk_review?.name || ""}
			</Text>
		</div>
	);
};

const SubjectCellRenderer = (params: ICellRendererParams<Review>) => {
	const review = params.data;
	if (!review) return null;

	const subjectID = review?.subject || "";
	const subjectName = review?.subject_name || "";

	return (
		<div className="flex h-full items-center gap-2">
			<Avatar size={20} id={subjectID} name={subjectName} />
			<Link href={toEmployeeUrl(subjectID)} isExternal>
				<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
					{subjectName}
				</Text>
			</Link>
		</div>
	);
};

const StatusCellRenderer = (params: ICellRendererParams<Review>) => {
	const review = params.data;
	if (!review) return null;

	const { start_date, completed, unassigned_report_form_count } = review;
	const parsedStartDate = parseISO(start_date);
	const currentDate = new Date();

	let status = "";
	if (completed) {
		status = "Completed";
	} else if (isBefore(parsedStartDate, currentDate)) {
		status = "Active";
	} else if (isAfter(parsedStartDate, currentDate)) {
		status = `Starting ${format(parsedStartDate, "MMM dd, yyyy")}`;
	}
	const unassigned = unassigned_report_form_count > 0 ? "(unassigned forms)" : "";
	const renderedText = `${status} ${unassigned}`;

	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
				{renderedText}
			</Text>
		</div>
	);
};

const getAllReviewsTableCols = (): PYColumnDef<Review>[] => {
	return [
		{
			field: "name",
			headerName: "Name",
			sortable: true,
			resizable: false,
			width: 366,
			cellRenderer: NameCellRenderer,
		},
		{
			field: "bulk_review.name",
			headerName: "Review Cycle",
			sortable: true,
			resizable: false,
			width: 200,
			cellRenderer: ReviewCycleCellRenderer,
		},
		{
			field: "subject_name",
			headerName: "Subject",
			sortable: true,
			resizable: false,
			width: 160,
			cellRenderer: SubjectCellRenderer,
		},
		{
			field: "start_date",
			headerName: "Start Date",
			sortable: true,
			resizable: false,
			width: 148,
			type: "date",
			cellRendererParams: {
				dateFormat: "MMM dd, yyyy h:mm a",
			},
		},
		{
			field: "status",
			headerName: "Status",
			sortable: false,
			resizable: false,
			width: 220,
			cellRenderer: StatusCellRenderer,
		},
	];
};

export default getAllReviewsTableCols;
